import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { motion } from "motion/react";

import { getText } from "shared/lib";
import {
  newNearbyTreeDescriptor,
  type TreeDescriptor,
} from "@/components/treeModule/lib";
import { animationVariants } from "@/components/wizard/widgets/lib";
import { isTreeItemValid } from "@/components/treeModule/treeList/lib";

import { type TreeModuleExpertResult } from "@/hooks";

import { Button } from "shared/components";
import {
  MultipleChoiceQuestion,
  NearbyTreeList,
} from "@/components/treeModule";

export type NearbyTreesTreesData = {
  areThereTreesNearby: string;
  areAnyTreesOutsidePropertyBoundary: string;
  treeDescriptors: Array<TreeDescriptor>;
};

export interface NearbyTreesContainerProps {
  councilCode: string;
  disabled?: boolean;
  nextButtonLabel?: string;
  onClickNext: (treeData: NearbyTreesTreesData) => void;
  showLoading?: boolean;
  submitting?: boolean;
  treeModuleExpertResult: TreeModuleExpertResult | undefined | null;
}

export function NearbyTreesContainer({
  councilCode,
  disabled,
  nextButtonLabel,
  onClickNext,
  showLoading,
  submitting,
  treeModuleExpertResult,
}: NearbyTreesContainerProps) {
  const [areThereTreesNearby, setAreThereTreesNearby] = useState("");
  const [
    areAnyTreesOutsidePropertyBoundary,
    setAreAnyTreesOutsidePropertyBoundary,
  ] = useState("");

  const newTreeOptions = useMemo(() => {
    return {
      distanceAboveGroundLevel:
        treeModuleExpertResult?.distance_above_ground_level.toString(),
      showTreeHeight: councilCode.toUpperCase() === "BAY",
      treeHeight: treeModuleExpertResult?.tree_height_trigger,
      useCircumference:
        treeModuleExpertResult?.measurement_method === "circumference",
    };
  }, [councilCode, treeModuleExpertResult]);

  const [treeDescriptors, setTreeDescriptors] = useState<Array<TreeDescriptor>>(
    () => {
      return [
        newNearbyTreeDescriptor(
          {
            title: "1",
            getText,
          },
          newTreeOptions,
        ),
      ];
    },
  );

  const [treesHaveBeenDeleted, setTreesHaveBeenDeleted] = useState(false);

  const isValid = useMemo(() => {
    if (areThereTreesNearby === "no") return true;

    if (areThereTreesNearby === "") return false;

    if (treeDescriptors.length === 0) return false;

    if (areAnyTreesOutsidePropertyBoundary === "") return false;

    return treeDescriptors.every((treeDescriptor) =>
      isTreeItemValid(treeDescriptor),
    );
  }, [
    areAnyTreesOutsidePropertyBoundary,
    areThereTreesNearby,
    treeDescriptors,
  ]);

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end md:mt-8"
      >
        <Button
          data-testid="next-button"
          disabled={submitting || showLoading || disabled || !isValid}
          onClick={() =>
            onClickNext({
              areThereTreesNearby,
              areAnyTreesOutsidePropertyBoundary,
              treeDescriptors,
            })
          }
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(nextButtonLabel || "Continue")}
        </Button>
      </motion.div>
    );
  };

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-nearby-trees"
    >
      <MultipleChoiceQuestion
        choices={[
          { label: getText("Yes"), value: "yes" },
          { label: getText("No"), value: "no" },
        ]}
        onChange={setAreThereTreesNearby}
        subTitle={getText(
          "This helps us determine if you need to take steps to protect trees during construction.",
        )}
        title={getText(
          "Besides trees you intend to remove or prune, are there any trees within 5 metres of your proposed works?",
        )}
        value={areThereTreesNearby}
      />

      {areThereTreesNearby === "yes" && (
        <>
          <NearbyTreeList
            onChangeTreeDescriptors={setTreeDescriptors}
            onTreeDeleted={() => setTreesHaveBeenDeleted(true)}
            newTreeOptions={newTreeOptions}
            subTitle={getText(
              "This helps us determine if they're protected, and the size of their protection zone.",
            )}
            title={getText("Describe these trees/vegetation")}
            treeDescriptors={treeDescriptors}
            treesHaveBeenDeleted={treesHaveBeenDeleted}
          />

          <MultipleChoiceQuestion
            choices={[
              { label: getText("Yes"), value: "yes" },
              { label: getText("No"), value: "no" },
            ]}
            onChange={setAreAnyTreesOutsidePropertyBoundary}
            subTitle={getText(
              "E.g. on the neighbour's property, or on the nature strip near the road.",
            )}
            title={getText(
              "Are any of these trees outside the property's boundaries?",
            )}
            value={areAnyTreesOutsidePropertyBoundary}
          />
        </>
      )}

      {renderFooter()}
    </div>
  );
}
